import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import {
    Order,
    OrderCreateInput,
    OrderExecutionResult,
    OrderFilters,
    OrderSummary,
} from '../../models/order';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';
import { useSnackbar } from 'notistack';

// CRM-specific order creation for CRM users creating trades for customers
const createCrmOrder = async (input: OrderCreateInput) => {
    try {
        const { data } = await httpClient.post<ApiResponse<OrderExecutionResult>>(
            API_ENDPOINTS.CRM.ORDERS.CREATE,
            input
        );
        return data.data;
    } catch (error: any) {
        // Log the error for debugging
        console.error('CRM Order creation error:', {
            input,
            error: error.message,
            status: error.status,
            data: error.data,
        });

        // Re-throw the error with enhanced information
        throw error;
    }
};

// CRM-specific order listing
const fetchCrmOrders = async (filters?: OrderFilters) => {
    const { data } = await httpClient.get<PaginatedResponse<Order>>(API_ENDPOINTS.CRM.ORDERS.LIST, {
        params: filters,
    });
    return data;
};

// CRM-specific order history
const fetchCrmOrderHistory = async (filters?: OrderFilters) => {
    const { data } = await httpClient.get<PaginatedResponse<Order>>(
        API_ENDPOINTS.CRM.ORDERS.HISTORY,
        { params: filters }
    );
    return data;
};

// CRM-specific order summary
const fetchCrmOrderSummary = async (period?: string) => {
    const { data } = await httpClient.get<ApiResponse<OrderSummary>>(
        API_ENDPOINTS.CRM.ORDERS.SUMMARY,
        { params: { period } }
    );
    return data.data;
};

// CRM-specific order details
const fetchCrmOrder = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<Order>>(API_ENDPOINTS.CRM.ORDERS.DETAILS(id));
    return data.data;
};

// CRM-specific order update
const updateCrmOrder = async ({
    id,
    input,
}: {
    id: string;
    input: {
        price?: number;
        lot_size?: number;
        stop_loss_price?: number;
        take_profit_price?: number;
    };
}) => {
    const { data } = await httpClient.put<ApiResponse<Order>>(
        API_ENDPOINTS.CRM.ORDERS.UPDATE(id),
        input
    );
    return data.data;
};

// CRM-specific order closure
const closeCrmOrder = async ({
    id,
    input,
}: {
    id: string;
    input: {
        close_type: 'full' | 'partial';
        close_amount?: number;
    };
}) => {
    const { data } = await httpClient.post<ApiResponse<Order>>(
        API_ENDPOINTS.CRM.ORDERS.CLOSE(id),
        input
    );
    return data.data;
};

// CRM-specific order deletion
const deleteCrmOrder = async (id: string) => {
    const { data } = await httpClient.delete<ApiResponse<Order>>(
        API_ENDPOINTS.CRM.ORDERS.DELETE(id)
    );
    return data.data;
};

// CRM Hooks
export const useCrmOrders = (filters?: OrderFilters) => {
    return useQuery<PaginatedResponse<Order>, Error>({
        queryKey: [API_ENDPOINTS.CRM.ORDERS.LIST, filters],
        queryFn: () => fetchCrmOrders(filters),
    });
};

export const useCrmOrderHistory = (filters?: OrderFilters) => {
    return useQuery<PaginatedResponse<Order>, Error>({
        queryKey: [API_ENDPOINTS.CRM.ORDERS.HISTORY, filters],
        queryFn: () => fetchCrmOrderHistory(filters),
    });
};

export const useCrmOrderSummary = (period?: string) => {
    return useQuery<OrderSummary, Error>({
        queryKey: [API_ENDPOINTS.CRM.ORDERS.SUMMARY, period],
        queryFn: () => fetchCrmOrderSummary(period),
    });
};

export const useCrmOrder = (id: string) => {
    return useQuery<Order, Error>({
        queryKey: [API_ENDPOINTS.CRM.ORDERS.DETAILS(id)],
        queryFn: () => fetchCrmOrder(id),
        enabled: !!id,
    });
};

export const useCreateCrmOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<OrderExecutionResult, Error, OrderCreateInput>({
        mutationFn: createCrmOrder,
        onSuccess: () => {
            // Invalidate all CRM trade queries to refresh all trade lists
            queryClient.invalidateQueries({ queryKey: ['crm-trades'] });
            // Also invalidate any trade summary queries
            queryClient.invalidateQueries({ queryKey: ['crm-trades-summary'] });
        },
        onError: (error: any) => {
            // Log mutation errors
            console.error('Mutation error in useCreateCrmOrder:', error);
        },
    });
};

export const useUpdateCrmOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<
        Order,
        Error,
        {
            id: string;
            input: {
                price?: number;
                lot_size?: number;
                stop_loss_price?: number;
                take_profit_price?: number;
            };
        }
    >({
        mutationFn: updateCrmOrder,
        onSuccess: (_data, variables) => {
            // Invalidate order details for this order
            if (variables?.id) {
                queryClient.invalidateQueries({
                    queryKey: [API_ENDPOINTS.CRM.ORDERS.DETAILS(variables.id)],
                });
            }

            // Invalidate any orders list/history queries regardless of current filters
            queryClient.invalidateQueries({
                predicate: q =>
                    Array.isArray(q.queryKey) &&
                    typeof q.queryKey[0] === 'string' &&
                    (q.queryKey[0] === API_ENDPOINTS.CRM.ORDERS.LIST ||
                        q.queryKey[0] === API_ENDPOINTS.CRM.ORDERS.HISTORY),
            });

            // Also invalidate generic orders namespace if used elsewhere
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS] });
        },
    });
};

export const useCloseCrmOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<
        Order,
        Error,
        {
            id: string;
            input: {
                close_type: 'full' | 'partial';
                close_amount?: number;
            };
        }
    >({
        mutationFn: closeCrmOrder,
        onSuccess: () => {
            // Invalidate the orders list query specifically
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS.LIST] });
            // Also invalidate any order-related queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS] });
        },
    });
};

export const useDeleteCrmOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<Order, Error, string>({
        mutationFn: deleteCrmOrder,
        onSuccess: () => {
            // Invalidate the orders list query specifically
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS.LIST] });
            // Also invalidate any order-related queries
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS] });
        },
    });
};

// Bulk operations
const bulkCancelCrmOrders = async (orderIds: string[]) => {
    const { data } = await httpClient.post<ApiResponse>(API_ENDPOINTS.CRM.ORDERS.BULK_CANCEL, {
        order_ids: orderIds,
    });
    return data;
};

const bulkModifyCrmOrders = async (params: {
    orderIds: string[];
    lot_size?: number;
    price?: number;
    stop_price?: number;
    stop_loss_price?: number;
    take_profit_price?: number;
    leverage?: number;
    expires_at?: string;
    post_only?: boolean;
    reduce_only?: boolean;
}) => {
    const { data } = await httpClient.post<ApiResponse>(API_ENDPOINTS.CRM.ORDERS.BULK_MODIFY, {
        order_ids: params.orderIds,
        ...Object.fromEntries(Object.entries(params).filter(([key]) => key !== 'orderIds')),
    });
    return data;
};

export const useBulkCancelCrmOrders = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: bulkCancelCrmOrders,
        onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS] });

            enqueueSnackbar(data.data?.message || 'Bulk cancel completed successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to bulk cancel orders', {
                variant: 'error',
            });
        },
    });
};

export const useBulkModifyCrmOrders = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: bulkModifyCrmOrders,
        onSuccess: data => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS.LIST] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.CRM.ORDERS] });

            enqueueSnackbar(data.data?.message || 'Bulk modify completed successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to bulk modify orders', {
                variant: 'error',
            });
        },
    });
};
